/**
 * Messages the application sends by itself when something happens (messaging
 * Phase 5).
 *
 * One registry, read by the server's `AutomationService` (whether to send,
 * and to whom), Settings → Messaging → Automations (the switches) and the
 * templates console (the wording each one sends, by `templates`). A new
 * automation is an entry here, a template for its wording and one call where
 * the thing happens.
 *
 * ⚠️ **What reaches the office is on by default; what reaches anybody else is
 * off until an administrator turns it on.** A producer can switch an office
 * notice off for themselves in their notification preferences; a client,
 * vendor or guest cannot, so nothing starts writing to them without the
 * office choosing it.
 */
export const AUTOMATIONS = [
    {
        key: 'payment.received.office',
        label: 'Payment received',
        trigger: 'A client payment is recorded',
        audience: 'office',
        templates: ['client.payment.received'],
    },
    {
        key: 'payment.receipt.client',
        label: 'Payment receipt',
        trigger: 'A client payment is recorded',
        audience: 'client',
        templates: ['payment.receipt'],
        options: [
            {
                key: 'showBalance',
                label: 'Include the balance still due (only where the client sees the budget)',
                defaultValue: true,
            },
        ],
    },
    {
        key: 'questionnaire.submitted',
        label: 'Questionnaire submitted',
        trigger: 'A client submits a questionnaire',
        audience: 'office',
        templates: ['questionnaire.submitted'],
    },
    {
        key: 'expense.client-decision',
        label: 'Client approved or rejected an expense',
        trigger: 'A client approves or rejects an expense in the portal',
        audience: 'office',
        templates: ['expense.approved', 'expense.rejected'],
    },
    {
        key: 'timeline.assigned',
        label: 'Timeline entry assigned',
        trigger: 'Someone is assigned a timeline entry',
        audience: 'assignee',
        templates: ['timeline.assigned'],
    },
    {
        key: 'kibuddim.assigned',
        label: 'Kibbud assigned',
        trigger: 'Someone is assigned a kibbud',
        audience: 'assignee',
        templates: ['kibuddim.assigned'],
    },
    {
        key: 'vendor.added',
        label: 'Vendor added to an event',
        trigger: 'A vendor is added to an event',
        audience: 'vendor',
        templates: ['vendor.added'],
        options: [
            {
                key: 'includeUploadLink',
                label: 'Include a link for the vendor to upload files',
                defaultValue: true,
            },
        ],
    },
    {
        key: 'client.added',
        label: 'Client welcome',
        trigger: 'A client is added to an event',
        audience: 'client',
        templates: ['client.welcome'],
    },
    {
        key: 'event.status.clients',
        label: 'Event status changed',
        trigger: "An event's status changes",
        audience: 'client',
        templates: ['event.status-changed'],
    },
    // ── Scheduled (Phase 5b): the morning run, from the office's digest hour ──
    {
        key: 'questionnaire.reminder',
        label: 'Questionnaire reminder',
        trigger: 'A questionnaire sent to a client is still not submitted',
        audience: 'client',
        templates: ['questionnaire.reminder'],
        schedule: { label: '{n} days after it was sent', defaultDays: 5, min: 1, max: 60 },
    },
    {
        key: 'vendor.payment.due',
        label: 'Vendor payment due',
        trigger: 'An expense with a balance left is coming due',
        audience: 'office',
        templates: ['expense.payment-due'],
        schedule: { label: '{n} days before it is due', defaultDays: 3, min: 0, max: 30 },
    },
    {
        key: 'quote.reminder',
        label: 'Quote reminder',
        trigger: 'A vendor asked for a quote has not sent it',
        audience: 'vendor',
        templates: ['vendor.quote_reminder'],
        schedule: { label: '{n} days after it was asked for', defaultDays: 5, min: 1, max: 30 },
    },
    {
        key: 'client.balance.due',
        label: 'Client balance due',
        trigger: 'An event is coming up and its client still owes',
        audience: 'client',
        templates: ['client.balance-due'],
        schedule: { label: '{n} days before the event', defaultDays: 14, min: 1, max: 60 },
    },
    {
        key: 'vendor.briefing',
        label: 'Vendor briefing',
        trigger: 'An event is about to happen',
        audience: 'vendor',
        templates: ['vendor.briefing'],
        schedule: { label: '{n} days before the event', defaultDays: 1, min: 1, max: 7 },
        options: [
            { key: 'attachTimeline', label: 'Attach the timeline', defaultValue: true },
            {
                key: 'attachSetup',
                label: 'Attach the floor plan setup report (head counts and meals — no guest names)',
                defaultValue: true,
            },
        ],
    },
    {
        key: 'client.thank-you',
        label: 'Thank you after the event',
        trigger: 'An event has ended',
        audience: 'client',
        templates: ['client.thank-you'],
        schedule: { label: '{n} days after the event', defaultDays: 1, min: 1, max: 14 },
        options: [
            {
                key: 'askForReview',
                label: 'Ask for a Google review (needs the Google place id in App Settings)',
                defaultValue: true,
            },
        ],
    },
];
/** The audiences that are the office's own, and so on unless switched off. */
const OFFICE_AUDIENCES = new Set(['office', 'assignee']);
export function automationDefaultEnabled(definition) {
    return OFFICE_AUDIENCES.has(definition.audience);
}
export function isAutomationKey(value) {
    return typeof value === 'string' && AUTOMATIONS.some((a) => a.key === value);
}
export function automationDefinition(key) {
    return AUTOMATIONS.find((a) => a.key === key);
}
/** A scheduled automation's days held to its bounds; a value that is not a whole number reads as the default. */
export function clampAutomationDays(schedule, value) {
    if (typeof value !== 'number' || !Number.isInteger(value))
        return schedule.defaultDays;
    return Math.min(schedule.max, Math.max(schedule.min, value));
}
/**
 * The setting in force: what was stored, else the defaults. An option stored
 * that the registry no longer declares is dropped, and one never set takes its
 * default, so a new option reads the same whether or not anybody saved since.
 */
export function resolveAutomationSetting(key, stored) {
    const definition = automationDefinition(key);
    const saved = stored?.options && typeof stored.options === 'object'
        ? stored.options
        : {};
    const options = {};
    for (const option of definition.options ?? []) {
        const value = saved[option.key];
        options[option.key] = typeof value === 'boolean' ? value : option.defaultValue;
    }
    return {
        key,
        enabled: stored ? stored.enabled : automationDefaultEnabled(definition),
        options,
        days: definition.schedule ? clampAutomationDays(definition.schedule, stored?.days) : null,
    };
}
//# sourceMappingURL=automations.js.map